import React, { useEffect } from 'react';

import type { SSRInput } from 'utils/ssr';
import { getSSRInput } from 'utils/ssr';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	ConsentBanner,
	initializeControls,
	getCookie,
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	shouldRenderBannerPrefetched,
} from '@atlassian/browser-storage-controls';
import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls';

/**
 * Feature gate: csm-cm04-use-atlassian-browser-storage-controls
 *
 * Enables Atlassian-managed browser storage controls (webStorageEnabled) for the
 * CSM/JSM Help Center cookie consent banner.
 *
 * Note: registered as kebab-case in Statsig/Switcheroo; do not convert to snake_case.
 *
 * Rollout: BEARS-1324
 * Owner: @team-bears
 * Cleanup: FFCLEANUP-122701
 */
const CSM_CM04_USE_ATLASSIAN_BROWSER_STORAGE_CONTROLS =
	'csm-cm04-use-atlassian-browser-storage-controls';
const isCsmCm04UseAtlassianBrowserStorageControlsEnabled = (): boolean =>
	FeatureGates.checkGate(CSM_CM04_USE_ATLASSIAN_BROWSER_STORAGE_CONTROLS);

// Soaking gate: enables webStorage + indexedDB consent enforcement.
const CSM_CM04_BSC_WEBSTORAGE_INDEXEDDB = 'csm-cm04-bsc-webstorage-indexeddb';
const isCsmCm04BscWebStorageIndexedDbEnabled = (): boolean =>
	FeatureGates.checkGate(CSM_CM04_BSC_WEBSTORAGE_INDEXEDDB);

// Soaking gate: analytics-only mode, disables webStorage + indexedDB consent checks.
const CSM_CM04_BSC_ANALYTICS_ONLY = 'csm-cm04-bsc-analytics-only';
const isCsmCm04BscAnalyticsOnlyEnabled = (): boolean =>
	FeatureGates.checkGate(CSM_CM04_BSC_ANALYTICS_ONLY);

// Phase-4 gate: when true, blocks uncategorized web-storage/indexedDB keys (disables the allow-uncategorized overrides).
const CSM_CM04_BSC_BLOCK_UNCATEGORIZED = 'csm-cm04-bsc-block-uncategorized';
const isCsmCm04BscBlockUncategorizedEnabled = (): boolean =>
	FeatureGates.checkGate(CSM_CM04_BSC_BLOCK_UNCATEGORIZED);

export const SSRSafeJSMCookieConsentBanner = () => {
	const ssrInput: SSRInput | undefined = getSSRInput();
	const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY) || ssrInput?.cookiesConsentToken;
	const fallbackConsentToken =
		getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY) || ssrInput?.cookiesFallbackToken;

	const cookieData: PrefetchedConsentPreferenceData = consentToken
		? {
				consentToken,
				fallbackConsentToken,
			}
		: {
				fallbackConsentToken,
			};

	useEffect(() => {
		if (!isCsmCm04UseAtlassianBrowserStorageControlsEnabled()) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			initializeControls({ product: 'jira' });
			return;
		}

		const webStorageIndexedDbGate = isCsmCm04BscWebStorageIndexedDbEnabled();
		const analyticsOnlyGate = isCsmCm04BscAnalyticsOnlyEnabled();
		const blockUncategorizedGate = isCsmCm04BscBlockUncategorizedEnabled();
		// Either soaking gate enables storage; consent checks are only disabled during analytics-only soaking.
		const webStorageIndexedDbEnabled = webStorageIndexedDbGate || analyticsOnlyGate;
		const analyticsOnly = analyticsOnlyGate && !webStorageIndexedDbGate;
		// Phase-4: uncategorized keys are only allowed while storage is enabled and the block gate is FAIL.
		const allowUncategorizedItems = webStorageIndexedDbEnabled && !blockUncategorizedGate;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		initializeControls({
			product: 'jira',
			webStorageEnabled: webStorageIndexedDbEnabled,
			indexedDbEnabled: webStorageIndexedDbEnabled,
			disableWebStorageConsentChecks: analyticsOnly,
			disableIndexedDbConsentChecks: analyticsOnly,
			allowUncategorizedItemsInWebStorageOverrides: allowUncategorizedItems,
			allowUncategorizedItemsInIndexedDBOverrides: allowUncategorizedItems,
		});
	}, []);

	// The case of only the fallbackConsentToken being present is handled differently on the
	// server and on the client. So in order to SSR the same way, we have handle this case
	// specially. Only fallback token being present on the client results in banner not being shown.
	// However, if we pass that cookie data into the initialPreferences on the client, there is a short
	// flicker of the consent banner. So we also have to special case this on the client.
	const onlyFallbackTokenPresent = Boolean(fallbackConsentToken && !consentToken);

	if (__SERVER__) {
		const shouldRenderBanner = onlyFallbackTokenPresent
			? false
			: shouldRenderBannerPrefetched(cookieData);
		if (shouldRenderBanner) {
			return (
				<div data-testid={'jsm-cookie-consent-banner-wrapper'}>
					<ConsentBanner product="jira" initialPreferences={cookieData} />
				</div>
			);
		}
		return <div data-testid={'jsm-cookie-consent-banner-wrapper'}></div>;
	}

	return (
		// Wrapper needed for stable DOM to rehydrate. Otherwise, rehydration failures may break the entire page when
		// this returns a banner in SSR and no banner/null in CSR (or the other way around).
		<div data-testid={'jsm-cookie-consent-banner-wrapper'}>
			{onlyFallbackTokenPresent ? (
				<ConsentBanner product="jira" />
			) : (
				<ConsentBanner product="jira" initialPreferences={cookieData} />
			)}
		</div>
	);
};
